import type { FieldArrayWithId, UseFormReturn } from "react-hook-form";
import { RepairIntakeSection1 } from "@/pages/public/repairIntakeForm/RepairIntakeSection1";
import { RepairIntakeSection2 } from "@/pages/public/repairIntakeForm/RepairIntakeSection2";
import { RepairIntakeSection3 } from "@/pages/public/repairIntakeForm/RepairIntakeSection3";
import { RepairIntakeSection4 } from "@/pages/public/repairIntakeForm/RepairIntakeSection4";
import { RepairIntakeSection5 } from "@/pages/public/repairIntakeForm/RepairIntakeSection5";
import { RepairIntakeSection6 } from "@/pages/public/repairIntakeForm/RepairIntakeSection6";
import { RepairIntakeSection7 } from "@/pages/public/repairIntakeForm/RepairIntakeSection7";
import { RepairIntakeSection8 } from "@/pages/public/repairIntakeForm/RepairIntakeSection8";
import { RepairIntakeSection9 } from "@/pages/public/repairIntakeForm/RepairIntakeSection9";
import type { SectionKey } from "@/pages/public/repairIntakeForm/repairIntakeFormConstants";
import type { RepairIntakeFormData } from "@/store/useRepairIntakeStore";

type RepairIntakeStepFieldsProps = {
  currentStepKey: SectionKey;
  form: UseFormReturn<RepairIntakeFormData>;
  componentTypes: string[];
  repairTypes: string[];
  defectTypes: string[];
  utReadingsAttached: RepairIntakeFormData["section2"]["utReadingsAttached"];
  activeLeak: RepairIntakeFormData["section6"]["activeLeak"];
  desiredRepairLife: string;
  goodPipeOnLandingZones: RepairIntakeFormData["section7"]["goodPipeOnLandingZones"];
  attachments: RepairIntakeFormData["section9"]["attachments"];
  componentEntryIds: string[];
  componentEntryCount: number;
  defectLengthFields: FieldArrayWithId<RepairIntakeFormData, "section5.defectLengthEntries", "id">[];
  desiredRepairLengthFields: FieldArrayWithId<RepairIntakeFormData, "section5.desiredRepairLengthEntries", "id">[];
  onToggleMultiSelect: (
    fieldName: "section1.componentTypes" | "section3.repairTypes" | "section5.defectTypes",
    value: string
  ) => void;
  onUpdateDesiredRepairLife: (value: string) => void;
  onAddComponent: () => void;
  onRemoveComponent: (index: number) => void;
  onAddAttachment: (payload: { file: File }) => void;
  onRemoveAttachment: (index: number) => void;
};

export function RepairIntakeStepFields({
  currentStepKey,
  form,
  componentTypes,
  repairTypes,
  defectTypes,
  utReadingsAttached,
  activeLeak,
  desiredRepairLife,
  goodPipeOnLandingZones,
  attachments,
  componentEntryIds,
  componentEntryCount,
  defectLengthFields,
  desiredRepairLengthFields,
  onToggleMultiSelect,
  onUpdateDesiredRepairLife,
  onAddComponent,
  onRemoveComponent,
  onAddAttachment,
  onRemoveAttachment,
}: RepairIntakeStepFieldsProps) {
  switch (currentStepKey) {
    case "section1":
      return (
        <RepairIntakeSection1
          form={form}
          componentTypes={componentTypes}
          componentEntryCount={componentEntryCount}
          componentEntryIds={componentEntryIds}
          onToggleComponentType={(value) => onToggleMultiSelect("section1.componentTypes", value)}
          onAddComponent={onAddComponent}
          onRemoveComponent={onRemoveComponent}
        />
      );
    case "section2":
      return <RepairIntakeSection2 form={form} utReadingsAttached={utReadingsAttached} />;
    case "section3":
      return (
        <RepairIntakeSection3
          form={form}
          repairTypes={repairTypes}
          onToggleRepairType={(value) => onToggleMultiSelect("section3.repairTypes", value)}
        />
      );
    case "section4":
      return <RepairIntakeSection4 form={form} />;
    case "section5":
      return (
        <RepairIntakeSection5
          form={form}
          defectTypes={defectTypes}
          defectLengthFields={defectLengthFields}
          desiredRepairLengthFields={desiredRepairLengthFields}
          onToggleDefectType={(value) => onToggleMultiSelect("section5.defectTypes", value)}
        />
      );
    case "section6":
      return <RepairIntakeSection6 form={form} activeLeak={activeLeak} />;
    case "section7":
      return (
        <RepairIntakeSection7
          form={form}
          desiredRepairLife={desiredRepairLife}
          goodPipeOnLandingZones={goodPipeOnLandingZones}
          onUpdateDesiredRepairLife={onUpdateDesiredRepairLife}
        />
      );
    case "section8":
      return <RepairIntakeSection8 form={form} />;
    case "section9":
      return (
        <RepairIntakeSection9
          attachments={attachments}
          errorMessage={form.formState.errors.section9?.attachments?.message}
          onAddAttachment={onAddAttachment}
          onRemoveAttachment={onRemoveAttachment}
        />
      );
  }
}
